import { useState, useEffect, useRef } from 'preact/hooks';
import { getConfig } from '../config';
import { register, getCountries, getStates } from '../api';
import { emit } from '../analytics';
import { SearchableSelect } from './SearchableSelect';
import { themeTokens } from '../themeTokens';
import { t } from '../i18n';
import { getAntiBotEnabled, getAntiBotMinFillMs } from '../antiBot';
import { DeliveryNotice } from './DeliveryGate';
import { incompatibleDelivery, type DeliveryRestriction } from '../utils/deliveryEligibility';

function getSignupFormStyles() {
  const tk = themeTokens;
  return {
    form: { width: '100%', maxWidth: 'none', padding: '1.25rem', margin: '0 auto', boxSizing: 'border-box' as const },
    input: { display: 'block', width: '100%', maxWidth: '100%', minHeight: 'var(--sw-control-height)', padding: '0.5rem 0.75rem', border: `1px solid ${tk.border}`, borderRadius: tk.radius, fontSize: 'var(--sw-font-size-sm)', lineHeight: 'var(--sw-line-height)', boxSizing: 'border-box' as const },
    label: { display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: 'var(--sw-font-size-sm)', color: tk.text },
    sectionTitle: { marginBottom: '0.9rem', marginTop: '1.35rem', fontSize: 'var(--sw-font-size-sm)', fontWeight: 600, color: tk.text } as const,
    sectionTitleFirst: { marginBottom: '0.9rem', marginTop: 0, fontSize: 'var(--sw-font-size-sm)', fontWeight: 600, color: tk.text } as const,
  };
}

export function SignupView({ onSuccess, checkoutContext = false, deliveryCountry = '', deliveryRules = [] }: {
  onSuccess?: () => void; checkoutContext?: boolean; deliveryCountry?: string; deliveryRules?: DeliveryRestriction[];
}) {
  const config = getConfig();
  const formStartedAtRef = useRef(Date.now());
  const [formData, setFormData] = useState({
    title: 'Mr.',
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    b_address: '',
    b_zipcode: '',
    b_city: '',
    b_country: deliveryCountry,
    b_state: '',
    same_as_billing: true,
    s_address: '',
    s_zipcode: '',
    s_city: '',
    s_country: '',
    s_state: '',
    passwd1: '',
    passwd2: '',
    terms: false,
    sw_honey: '',
  });
  const [countries, setCountries] = useState<Record<string, string>>({});
  const [states, setStates] = useState<Record<string, Record<string, string>>>({});
  const submissionPendingRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const styles = getSignupFormStyles();
  const getInputStyle = (field: string) => ({ ...styles.input, ...(validationErrors[field] && { borderColor: themeTokens.danger }) });
  const shippingCountry = formData.same_as_billing ? formData.b_country : (formData.s_country || formData.b_country);
  const deliveryBlocked = incompatibleDelivery(deliveryRules, shippingCountry).length > 0;

  useEffect(() => {
    Promise.all([getCountries(config), getStates(config)]).then(([cRes, sRes]) => {
      if (cRes.status === 200 && cRes.result) setCountries(cRes.result);
      if (sRes.status === 200 && sRes.result) {
        const raw = sRes.result as Record<string, Record<string, string>> | Array<{ country_code: string; state_code: string; state: string }>;
        const byCountry: Record<string, Record<string, string>> = Array.isArray(raw)
          ? raw.reduce<Record<string, Record<string, string>>>((acc, row) => {
              const cc = row.country_code;
              if (!acc[cc]) acc[cc] = {};
              acc[cc][row.state_code] = row.state;
              return acc;
            }, {})
          : raw;
        setStates(byCountry);
      }
    });
  }, [config.apiUrl, config.siteKey]);

  const handleChange = (e: Event) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const value = target.type === 'checkbox' ? (target as HTMLInputElement).checked : target.value;
    setFormData({ ...formData, [target.name]: value });
    if (validationErrors[target.name]) {
      setValidationErrors({ ...validationErrors, [target.name]: '' });
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'b_country') next.b_state = '';
      if (name === 's_country') next.s_state = '';
      return next;
    });
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!formData.firstname) errors.firstname = t('signup.firstNameRequired');
    if (!formData.lastname) errors.lastname = t('signup.lastNameRequired');
    if (!formData.email) errors.email = t('signup.emailRequired');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = t('signup.emailInvalid');
    else if (formData.email.length > 128) errors.email = t('signup.emailInvalid');
    if (!formData.phone) errors.phone = t('signup.phoneRequired');
    else if (formData.phone.length < 3 || formData.phone.length > 30) errors.phone = t('signup.phoneRequired');
    if (!formData.b_address) errors.b_address = t('signup.addressRequired');
    if (!formData.b_zipcode) errors.b_zipcode = t('signup.zipcodeRequired');
    if (!formData.b_city) errors.b_city = t('signup.cityRequired');
    if (!formData.b_country) errors.b_country = t('signup.countryRequired');
    
    if (!formData.same_as_billing) {
      if (!formData.s_address) errors.s_address = t('signup.addressRequired');
      if (!formData.s_zipcode) errors.s_zipcode = t('signup.zipcodeRequired');
      if (!formData.s_city) errors.s_city = t('signup.cityRequired');
      if (!formData.s_country) errors.s_country = t('signup.countryRequired');
    }

    if (!formData.passwd1) errors.passwd1 = t('signup.passwordRequired');
    else if (formData.passwd1.length < 5) errors.passwd1 = t('signup.passwordMinLength');
    if (formData.passwd1 !== formData.passwd2) errors.passwd2 = t('signup.passwordMismatch');
    if (!formData.terms) errors.terms = t('signup.termsRequired');

    setValidationErrors(errors);
    if (checkoutContext && Object.keys(errors).length) {
      emit('sw:checkout-validation-error', {
        step: 'customer_data', error_count: Object.keys(errors).length, fields: Object.keys(errors),
      });
    }
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (submissionPendingRef.current) return;
    submissionPendingRef.current = true;
    let submissionSucceeded = false;
    try {
      setError('');

      if (deliveryBlocked) {
        setError(t('delivery.blocked'));
        return;
      }

      if (getAntiBotEnabled() && (formData.sw_honey.trim() !== '' || Date.now() - formStartedAtRef.current < getAntiBotMinFillMs())) {
        setError(t('api.genericError'));
        return;
      }

      if (!validate()) return;

      if (checkoutContext) emit('sw:checkout-submit', { step: 'customer_data' });
      setLoading(true);
      const { passwd1, passwd2, sw_honey, terms, ...signupFields } = formData;
      const res = await register(config, {
        ...signupFields,
        ...(formData.same_as_billing ? {
          s_country: formData.b_country, s_state: formData.b_state, s_city: formData.b_city,
          s_address: formData.b_address, s_zipcode: formData.b_zipcode,
        } : {}),
        passwd: passwd1,
        sw_form_ts: formStartedAtRef.current,
        sw_honey,
      });

      if (res.status === 200) {
        submissionSucceeded = true;
        emit('sw:signup');
        if (onSuccess) onSuccess();
      } else {
        const apiErrors = res.errors;
        if (apiErrors && typeof apiErrors === 'object') {
          const mapped: Record<string, string> = {};
          for (const [key, vals] of Object.entries(apiErrors)) {
            const field = key === 'passwd' ? 'passwd1' : key;
            const msg = Array.isArray(vals) ? vals[0] : vals;
            if (typeof msg === 'string' && msg) mapped[field] = msg;
          }
          if (Object.keys(mapped).length) {
            setValidationErrors((prev) => ({ ...prev, ...mapped }));
            if (checkoutContext) {
              emit('sw:checkout-validation-error', {
                step: 'customer_data', error_count: Object.keys(mapped).length, fields: Object.keys(mapped),
              });
            }
          }
        }
        setError(res.error ?? t('signup.failed'));
      }
    } catch {
      setError(t('api.networkError'));
    } finally {
      submissionPendingRef.current = submissionSucceeded;
      setLoading(false);
    }
  };

  const renderStateSelect = (countryCode: string, fieldName: string, value: string) => {
    const countryStates = states[countryCode];
    if (!countryStates || Object.keys(countryStates).length === 0) {
      return (
        <input
          type="text"
          style={getInputStyle(fieldName)}
          name={fieldName}
          value={value}
          onInput={handleChange}
          placeholder={t('signup.stateProvince')}
        />
      );
    }
    return (
      <SearchableSelect
        options={countryStates}
        value={value}
        onChange={(val) => handleSelectChange(fieldName, val)}
        name={fieldName}
        error={!!validationErrors[fieldName]}
        placeholder={t('signup.selectState')}
      />
    );
  };

  return (
    <div class="sw-tickets-shell">
    <form onSubmit={handleSubmit} noValidate style={styles.form} class="sw-surface-card sw-signup-card">
      <DeliveryNotice rules={deliveryRules} country={shippingCountry} />
      <input
        type="text"
        name="sw_honey"
        value={formData.sw_honey}
        onInput={handleChange}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
      />
      <div class="row sw-signup-card__grid">
        {/* Left Column: Personal & Billing */}
        <div class="col-md-6">
          <h5 style={styles.sectionTitleFirst}>{t('signup.personalInfo')}</h5>
          <div class="mb-3">
            <label style={styles.label}>{t('signup.title')}</label>
            <SearchableSelect
              options={{ 'Mr.': t('signup.mr'), 'Mrs.': t('signup.mrs'), 'Ms.': t('signup.ms') }}
              value={formData.title}
              onChange={(val) => handleSelectChange('title', val)}
              name="title"
              smallText
              searchable={false}
            />
          </div>
          <div class="mb-3">
            <label style={styles.label}>{t('signup.firstName')} <span class="text-danger">*</span></label>
            <input type="text" style={getInputStyle('firstname')} name="firstname" value={formData.firstname} onInput={handleChange} />
            {validationErrors.firstname && <div class="small text-danger mt-1">{validationErrors.firstname}</div>}
          </div>
          <div class="mb-3">
            <label style={styles.label}>{t('signup.lastName')} <span class="text-danger">*</span></label>
            <input type="text" style={getInputStyle('lastname')} name="lastname" value={formData.lastname} onInput={handleChange} />
            {validationErrors.lastname && <div class="small text-danger mt-1">{validationErrors.lastname}</div>}
          </div>

          <h5 style={styles.sectionTitle}>{t('signup.contactInfo')}</h5>
          <div class="mb-3">
            <label style={styles.label}>{t('signup.email')} <span class="text-danger">*</span></label>
            <input type="email" style={getInputStyle('email')} name="email" value={formData.email} onInput={handleChange} />
            {validationErrors.email && <div class="small text-danger mt-1">{validationErrors.email}</div>}
          </div>
          <div class="mb-3">
            <label style={styles.label}>{t('signup.phone')} <span class="text-danger">*</span></label>
            <input type="tel" style={getInputStyle('phone')} name="phone" value={formData.phone} onInput={handleChange} />
            {validationErrors.phone && <div class="small text-danger mt-1">{validationErrors.phone}</div>}
          </div>

          <h5 style={styles.sectionTitle}>{t('signup.billingAddress')}</h5>
          <div class="mb-3">
            <label style={styles.label}>{t('signup.address')} <span class="text-danger">*</span></label>
            <input type="text" style={getInputStyle('b_address')} name="b_address" value={formData.b_address} onInput={handleChange} />
            {validationErrors.b_address && <div class="small text-danger mt-1">{validationErrors.b_address}</div>}
          </div>
          <div class="row">
            <div class="col-6 mb-3">
              <label style={styles.label}>{t('signup.zipcode')} <span class="text-danger">*</span></label>
              <input type="text" style={getInputStyle('b_zipcode')} name="b_zipcode" value={formData.b_zipcode} onInput={handleChange} />
              {validationErrors.b_zipcode && <div class="small text-danger mt-1">{validationErrors.b_zipcode}</div>}
            </div>
            <div class="col-6 mb-3">
              <label style={styles.label}>{t('signup.city')} <span class="text-danger">*</span></label>
              <input type="text" style={getInputStyle('b_city')} name="b_city" value={formData.b_city} onInput={handleChange} />
              {validationErrors.b_city && <div class="small text-danger mt-1">{validationErrors.b_city}</div>}
            </div>
          </div>
          <div class="row">
            <div class="col-6 mb-3">
              <label style={styles.label}>{t('signup.country')} <span class="text-danger">*</span></label>
              <SearchableSelect
                options={countries}
                value={formData.b_country}
                onChange={(val) => handleSelectChange('b_country', val)}
                name="b_country"
                error={!!validationErrors.b_country}
                placeholder={t('signup.selectCountry')}
                required
              />
              {validationErrors.b_country && <div class="small text-danger mt-1">{validationErrors.b_country}</div>}
            </div>
            <div class="col-6 mb-3">
              <label style={styles.label}>{t('signup.state')}</label>
              {renderStateSelect(formData.b_country, 'b_state', formData.b_state)}
            </div>
          </div>
        </div>

        {/* Right Column: Shipping & Account */}
        <div class="col-md-6">
          <h5 style={styles.sectionTitleFirst}>{t('signup.shippingAddress')}</h5>
          <div class="form-check mb-3">
            <input class="form-check-input" type="checkbox" id="same_as_billing" name="same_as_billing" checked={formData.same_as_billing} onChange={handleChange} />
            <label class="form-check-label" for="same_as_billing">
              {t('signup.sameAsBilling')}
            </label>
          </div>

          {!formData.same_as_billing && (
            <div class="animate-fade-in">
              <div class="mb-3">
                <label style={styles.label}>{t('signup.address')} <span class="text-danger">*</span></label>
                <input type="text" style={getInputStyle('s_address')} name="s_address" value={formData.s_address} onInput={handleChange} />
                {validationErrors.s_address && <div class="small text-danger mt-1">{validationErrors.s_address}</div>}
              </div>
              <div class="row">
                <div class="col-6 mb-3">
                  <label style={styles.label}>{t('signup.zipcode')} <span class="text-danger">*</span></label>
                  <input type="text" style={getInputStyle('s_zipcode')} name="s_zipcode" value={formData.s_zipcode} onInput={handleChange} />
                  {validationErrors.s_zipcode && <div class="small text-danger mt-1">{validationErrors.s_zipcode}</div>}
                </div>
                <div class="col-6 mb-3">
                  <label style={styles.label}>{t('signup.city')} <span class="text-danger">*</span></label>
                  <input type="text" style={getInputStyle('s_city')} name="s_city" value={formData.s_city} onInput={handleChange} />
                  {validationErrors.s_city && <div class="small text-danger mt-1">{validationErrors.s_city}</div>}
                </div>
              </div>
              <div class="row">
                <div class="col-6 mb-3">
                  <label style={styles.label}>{t('signup.country')} <span class="text-danger">*</span></label>
                  <SearchableSelect
                    options={countries}
                    value={formData.s_country}
                    onChange={(val) => handleSelectChange('s_country', val)}
                    name="s_country"
                    error={!!validationErrors.s_country}
                    placeholder={t('signup.selectCountry')}
                    required
                  />
                  {validationErrors.s_country && <div class="small text-danger mt-1">{validationErrors.s_country}</div>}
                </div>
                <div class="col-6 mb-3">
                  <label style={styles.label}>{t('signup.state')}</label>
                  {renderStateSelect(formData.s_country, 's_state', formData.s_state)}
                </div>
              </div>
            </div>
          )}

          <h5 style={styles.sectionTitle}>{t('signup.privacyInfo')}</h5>
          <div class="mb-3">
            <label style={styles.label}>{t('signup.password')} <span class="text-danger">*</span></label>
            <input type="password" style={getInputStyle('passwd1')} name="passwd1" value={formData.passwd1} onInput={handleChange} />
            <div class="form-text">{t('signup.passwordHint')}</div>
            {validationErrors.passwd1 && <div class="small text-danger mt-1">{validationErrors.passwd1}</div>}
          </div>
          <div class="mb-3">
            <label style={styles.label}>{t('signup.confirmPassword')} <span class="text-danger">*</span></label>
            <input type="password" style={getInputStyle('passwd2')} name="passwd2" value={formData.passwd2} onInput={handleChange} />
            {validationErrors.passwd2 && <div class="small text-danger mt-1">{validationErrors.passwd2}</div>}
          </div>

          <div class="form-check mb-4">
            <input class={`form-check-input ${validationErrors.terms ? 'is-invalid' : ''}`} type="checkbox" id="terms" name="terms" checked={formData.terms} onChange={handleChange} />
            <label class="form-check-label" for="terms">
              {(() => {
                const tpl = t('signup.agreeTermsTemplate', "I agree with company's {terms}.");
                const [before, after = ''] = tpl.split('{terms}');
                return (
                  <>
                    {before}
                    <a href="/terms">{t('signup.agreeTermsLink')}</a>
                    {after}
                  </>
                );
              })()}
            </label>
            {validationErrors.terms && <div class="small text-danger mt-1 d-block">{validationErrors.terms}</div>}
          </div>

          {error && (
            <div class="alert alert-danger py-2 mb-3">
              <span style="display:inline-flex;align-items:center;gap:.4rem">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
                  <path d="M12 7v6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                  <circle cx="12" cy="16.5" r="1" fill="currentColor" />
                </svg>
                {error}
              </span>
            </div>
          )}

          <div class="d-grid">
            <button type="submit" class="btn btn-primary w-100" disabled={loading || deliveryBlocked}>
              {loading ? (
                <span><span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>{t('signup.creating')}</span>
              ) : (
                t('signup.createAccount')
              )}
            </button>
          </div>
        </div>
      </div>
    </form>
    </div>
  );
}
